import React, { useState, useEffect } from 'react';
import {
  Box, Typography, Paper, Button, IconButton, TextField, Chip,
  Dialog, DialogTitle, DialogContent, DialogActions,
  FormControl, InputLabel, Select, MenuItem, FormControlLabel, Switch,
  RadioGroup, Radio, Autocomplete, CircularProgress, Grid
} from '@mui/material';
import {
  Close as CloseIcon
} from '@mui/icons-material';
import { CreateNotificationPayload } from '../types';

interface CreateNotificationDialogProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (payload: CreateNotificationPayload) => Promise<void>;
  availablePharmacies: string[];
  creating: boolean;
}

const CreateNotificationDialog: React.FC<CreateNotificationDialogProps> = ({
  open,
  onClose,
  onSubmit,
  availablePharmacies,
  creating
}) => {
  const [message, setMessage] = useState('');
  const [status, setStatus] = useState<'normal' | 'important'>('important');
  const [sender, setSender] = useState(localStorage.getItem('username') || '');
  const [targetType, setTargetType] = useState<'all' | 'pharmacies' | 'roles'>('all');
  const [selectedPharmacies, setSelectedPharmacies] = useState<string[]>([]);
  const [selectedRole, setSelectedRole] = useState('user');
  const [hasExpiresAt, setHasExpiresAt] = useState(false);
  const [expiresAt, setExpiresAt] = useState('');

  useEffect(() => {
    if (open) {
      setSender(localStorage.getItem('username') || '');
    }
  }, [open]);

  const handleSubmit = async () => {
    let targetValues: string[] | null = null;
    if (targetType === 'pharmacies') {
      targetValues = selectedPharmacies;
    } else if (targetType === 'roles') {
      targetValues = [selectedRole];
    }

    await onSubmit({
      message: message.trim(),
      status,
      sender: sender.trim(),
      targetType,
      targetValues,
      expiresAt: hasExpiresAt && expiresAt ? expiresAt : null,
      createdBy: localStorage.getItem('username') || 'admin'
    });
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{ sx: { borderRadius: '8px', p: 0.5 } }}
    >
      <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', py: 1, px: 2, borderBottom: '1px solid #e2e8f0' }}>
        <Typography variant="subtitle1" sx={{ fontWeight: 700, fontSize: '0.95rem' }}>
          Новое уведомление
        </Typography>
        <IconButton size="small" onClick={onClose} sx={{ color: '#64748b' }}>
          <CloseIcon sx={{ fontSize: 18 }} />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, pt: 2, px: 2 }}>
        <TextField
          label="Текст уведомления *"
          multiline
          rows={3}
          fullWidth
          size="small"
          value={message}
          onChange={e => setMessage(e.target.value)}
          placeholder="Введите текст сообщения..."
          sx={{ '& .MuiInputBase-input': { fontSize: '0.875rem' } }}
        />

        <Grid container spacing={1.5}>
          <Grid item xs={6}>
            <FormControl fullWidth size="small">
              <InputLabel sx={{ fontSize: '0.85rem' }}>Важность</InputLabel>
              <Select
                value={status}
                label="Важность"
                onChange={e => setStatus(e.target.value as 'normal' | 'important')}
                sx={{ fontSize: '0.85rem' }}
              >
                <MenuItem value="important" sx={{ fontSize: '0.85rem' }}>Важное</MenuItem>
                <MenuItem value="normal" sx={{ fontSize: '0.85rem' }}>Обычное</MenuItem>
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={6}>
            <TextField
              label="Отправитель"
              size="small"
              fullWidth
              value={sender}
              onChange={e => setSender(e.target.value)}
              sx={{ '& .MuiInputBase-input': { fontSize: '0.85rem' } }}
            />
          </Grid>
        </Grid>

        <Paper variant="outlined" sx={{ p: 1.5, bgcolor: '#f8fafc', borderColor: '#e2e8f0', borderRadius: '6px' }}>
          <Typography variant="caption" sx={{ fontWeight: 600, color: '#475569', display: 'block', mb: 0.5 }}>
            Кому адресовано:
          </Typography>
          <RadioGroup
            row
            value={targetType}
            onChange={e => setTargetType(e.target.value as 'all' | 'pharmacies' | 'roles')}
            sx={{ mb: 1 }}
          >
            <FormControlLabel value="all" control={<Radio size="small" />} label={<Typography sx={{ fontSize: '0.8rem' }}>Всем</Typography>} />
            <FormControlLabel value="pharmacies" control={<Radio size="small" />} label={<Typography sx={{ fontSize: '0.8rem' }}>Аптекам</Typography>} />
            <FormControlLabel value="roles" control={<Radio size="small" />} label={<Typography sx={{ fontSize: '0.8rem' }}>Ролям</Typography>} />
          </RadioGroup>

          {targetType === 'pharmacies' && (
            <Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                <Typography variant="caption" sx={{ color: '#64748b', fontSize: '0.75rem' }}>
                  Номера аптек:
                </Typography>
                <Button
                  size="small"
                  onClick={() => setSelectedPharmacies(availablePharmacies)}
                  sx={{ textTransform: 'none', fontSize: '0.7rem', p: 0 }}
                >
                  Выбрать все ({availablePharmacies.length})
                </Button>
              </Box>
              <Autocomplete
                multiple
                size="small"
                options={availablePharmacies}
                value={selectedPharmacies}
                onChange={(_, val) => setSelectedPharmacies(val)}
                renderInput={params => (
                  <TextField {...params} placeholder="Поиск аптек..." size="small" sx={{ bgcolor: 'white' }} />
                )}
                renderTags={(val, getTagProps) =>
                  val.map((option, index) => (
                    <Chip
                      {...getTagProps({ index })}
                      key={option}
                      label={`№${option}`}
                      size="small"
                      sx={{ height: 18, fontSize: '0.67rem', bgcolor: '#e0e7ff', color: '#3730a3' }}
                    />
                  ))
                }
              />
            </Box>
          )}

          {targetType === 'roles' && (
            <FormControl fullWidth size="small" sx={{ bgcolor: 'white' }}>
              <InputLabel sx={{ fontSize: '0.85rem' }}>Роль</InputLabel>
              <Select
                value={selectedRole}
                label="Роль"
                onChange={e => setSelectedRole(e.target.value)}
                sx={{ fontSize: '0.85rem' }}
              >
                <MenuItem value="user" sx={{ fontSize: '0.85rem' }}>Аптеки (user)</MenuItem>
                <MenuItem value="storage" sx={{ fontSize: '0.85rem' }}>Склады (storage)</MenuItem>
              </Select>
            </FormControl>
          )}
        </Paper>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <FormControlLabel
            control={
              <Switch
                size="small"
                checked={hasExpiresAt}
                onChange={e => setHasExpiresAt(e.target.checked)}
              />
            }
            label={<Typography sx={{ fontSize: '0.8rem', color: '#475569' }}>Ограничить по времени</Typography>}
          />

          {hasExpiresAt && (
            <TextField
              type="datetime-local"
              size="small"
              value={expiresAt}
              onChange={e => setExpiresAt(e.target.value)}
              sx={{ '& .MuiInputBase-input': { py: 0.5, fontSize: '0.8rem' } }}
            />
          )}
        </Box>
      </DialogContent>

      <DialogActions sx={{ px: 2, py: 1.2, borderTop: '1px solid #e2e8f0' }}>
        <Button size="small" onClick={onClose} sx={{ color: '#64748b', textTransform: 'none', fontSize: '0.8rem' }}>
          Отмена
        </Button>
        <Button
          size="small"
          variant="contained"
          disabled={creating}
          onClick={handleSubmit}
          sx={{
            bgcolor: '#2563eb',
            textTransform: 'none',
            fontWeight: 600,
            fontSize: '0.8rem',
            px: 2,
            borderRadius: '6px',
            '&:hover': { bgcolor: '#1d4ed8' }
          }}
        >
          {creating ? <CircularProgress size={16} color="inherit" /> : 'Опубликовать'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default CreateNotificationDialog;
